/**
 * جذر التطبيق — الجلسة والتوجيه.
 *
 * القاعدة: مفيش شاشة بتتعرض قبل ما نعرف مين اللي داخل. `GET /auth/me`
 * بيتنده مرة عند الإقلاع، وكل الشاشات بتقرا منه.
 *
 * التوجيه كله من هنا ومن جدول `ROUTES` جوّه `lib/router.tsx` — مصدر واحد:
 *   • `active`                          ⇒ الشِل، وأول شاشة «مكتبي»
 *   • `pending_email` / `pending_approval` ⇒ شاشة «تحت المراجعة» وبس
 *   • مش مسجّل                           ⇒ الدخول أو التسجيل
 *
 * ⚠️ الشاشات ما بتوجّهش نفسها بعد الدخول. لو شاشة الدخول قالت «/desk» بنفسها،
 *    الحساب المعلّق كان هيهبط على شِل كامل لحد ما التوجيه يلحق يصحّحه.
 */
import { useEffect, type ComponentType, type ReactNode } from "react";
import {
  RouterProvider,
  decideRedirect,
  matchRoute,
  routeAllows,
  useRouter,
  type RouteDef,
} from "@/lib/router";
import { SessionProvider, useSession } from "@/lib/session";
import { startPulse } from "@/lib/pulse";
import { accountStatusOf, isPendingStatus } from "@/api/auth";
import { Shell } from "@/components/Shell";
import { MustChangePassword } from "@/screens/MustChangePassword";
import { NotFound } from "@/screens/NotFound";
import { UnderConstruction } from "@/screens/UnderConstruction";
import { ErrorState, LoadingState, OfflineState } from "@/components/states/ScreenStates";
import "@/styles/tokens.css";
import "@/styles/shell.css";
import "@/styles/states.css";

export default function App() {
  return (
    <RouterProvider>
      <SessionProvider>
        <Routes />
      </SessionProvider>
    </RouterProvider>
  );
}

/* ══════════════════════════════════════════════════════════════════
   سجل الشاشات — اربط ملفك بمساره بلا ما تلمس الراوتنج
   ══════════════════════════════════════════════════════════════════

   كل مسار في `ROUTES` بيقول اسم شاشته (`screen`). بنجيب الملف من
   `src/screens/<screen>.tsx` وقت البناء:
     • الملف موجود والتصدير موجود  ⇒ الشاشة بتشتغل
     • الملف مش موجود              ⇒ «لسه بتتجهّز» (مش «مش موجودة»)
     • الملف موجود والتصدير ناقص   ⇒ ⛔ خطأ صريح + ERROR في السجل

   ⚠️ الحالة التالتة دي بالذات ممنوع تعدّي كأنها «لسه بتتجهّز» — الشاشة
      اتكتبت فعلًا واسم التصدير غلط، والرسالة الودّية كانت هتخبّي العطل
      عن اللي كاتبها.
*/

const SCREEN_MODULES = import.meta.glob("./screens/*.tsx", { eager: true }) as Record<
  string,
  Record<string, unknown>
>;

type Resolved =
  | { kind: "ready"; Component: ComponentType }
  | { kind: "missing" }
  | { kind: "broken" };

/** الحل بيتحسب مرة واحدة لكل شاشة — والـERROR ما يتكررش كل رندر */
const resolveCache = new Map<string, Resolved>();

function resolveScreen(name: string): Resolved {
  const cached = resolveCache.get(name);
  if (cached) return cached;

  const mod = SCREEN_MODULES[`./screens/${name}.tsx`];
  let result: Resolved;

  if (!mod) {
    result = { kind: "missing" };
  } else {
    const exported = mod[name] ?? mod["default"];
    if (typeof exported === "function") {
      result = { kind: "ready", Component: exported as ComponentType };
    } else {
      console.error(
        `[router] الملف src/screens/${name}.tsx موجود بس مفيش فيه تصدير اسمه «${name}» ولا default —` +
          ` الموجود: ${Object.keys(mod).join(", ") || "لا شيء"}`,
      );
      result = { kind: "broken" };
    }
  }

  resolveCache.set(name, result);
  return result;
}

/* ══════════════════ التوجيه ══════════════════ */

function Routes() {
  const { status, me, reload } = useSession();
  const { path, navigate } = useRouter();

  const route = matchRoute(path);
  const pending = isPendingStatus(accountStatusOf(me));

  // القرار نفسه في `decideRedirect` — دالة نقية متجرَّبة لوحدها
  useEffect(() => {
    const to = decideRedirect({ status, pending, path });
    if (to !== null && to !== path) navigate(to, { replace: true });
  }, [status, pending, path, navigate]);

  // 💓 النبضة بتشتغل للموظف النشط بس — ومش بتبعت غير لو فيه حركة فعلية
  useEffect(() => {
    if (status !== "authed" || pending || me?.must_change_password) return;
    return startPulse();
  }, [status, pending, me?.must_change_password]);

  if (status === "loading") return <BootScreen />;

  if (status === "offline") {
    // ⚠️ شاشات المصادقة بتفضل شغّالة وإحنا مقطوعين — الرسالة بتظهر جوّه
    //    النموذج نفسه بدل شاشة انقطاع عامة الموظف ما يعرفش يعمل بيها حاجة.
    if (route && routeAllows(route.access, "guest")) return <ScreenOutlet route={route} />;
    return (
      <BareCenter>
        <OfflineState onRetry={() => void reload()} fill />
      </BareCenter>
    );
  }

  if (status === "guest") {
    if (route && routeAllows(route.access, "guest")) return <ScreenOutlet route={route} />;
    return <BootScreen />; // لحظة التحويل لشاشة الدخول
  }

  if (pending) {
    if (route && routeAllows(route.access, "pending")) return <ScreenOutlet route={route} />;
    return <BootScreen />; // لحظة التحويل لشاشة «تحت المراجعة»
  }

  /* 🔒 **قفل كلمة السر المؤقتة — قبل أي شاشة تانية.**
     الكلمة دي المدير كتبها وشافها وبعتها في رسالة. القفل هنا (مش في شاشة
     الدخول) عشان التحديث أو الرابط المباشر ما يلفّش حواليه، والحالة نفسها
     بتيجي من `/auth/me` مع كل تحميل. ⛔ مفيش زرار «بعدين». */
  if (me?.must_change_password) {
    return (
      <BareCenter>
        <MustChangePassword />
      </BareCenter>
    );
  }

  if (!route) {
    // الجذر بيتحوّل في الـeffect — أي رابط تاني مالوش شاشة فعلًا
    if (path === "/" || path === "") return <BootScreen />;
    return <NotFound />;
  }
  // ⛔ ممنوع مقارنة `route.access` بإيدها هنا — القرار في `routeAllows`
  //    وبس. التكرار هو اللي خلّى رابط التأكيد يعلّق على شاشة تحميل.
  if (!routeAllows(route.access, "active")) return <BootScreen />;

  return <ScreenOutlet route={route} />;
}

/** بيرندر شاشة المسار — أو بيقول الحقيقة لو لسه ما اتبنتش أو مكسورة */
function ScreenOutlet({ route }: { route: RouteDef }) {
  const resolved = resolveScreen(route.screen);

  if (resolved.kind === "ready") {
    const Screen = resolved.Component;
    return <Screen />;
  }

  if (resolved.kind === "broken") {
    const body = (
      <ErrorState
        messageAr={`شاشة «${route.title}» مش بتفتح`}
        hint="برجاء التواصل مع إدارة الأكاديمية"
        fill
      />
    );
    if (route.chrome === "bare") return <BareCenter>{body}</BareCenter>;
    return <Shell title={route.title}>{body}</Shell>;
  }

  return <UnderConstruction title={route.title} chrome={route.chrome} />;
}

/* ══════════════════ أغلفة العرض ══════════════════ */

/** شاشة الإقلاع — قبل ما نعرف مين اللي داخل، وفي لحظات التحويل */
function BootScreen() {
  return (
    <div className="app" dir="rtl">
      <div className="body">
        <div className="content">
          <LoadingState rows={4} fill />
        </div>
      </div>
    </div>
  );
}

/** توسيط بلا شِل — لشاشات المصادقة وحالات ما قبل الدخول */
function BareCenter({ children }: { children: ReactNode }) {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}
    >
      <div style={{ width: 480, maxWidth: "100%", display: "flex" }}>{children}</div>
    </div>
  );
}
